// contexts/FilterContext.js
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { customAPI } from "services/utils/CustomAPI";
import { showToast } from "services/utils/Status";
import { fetchFilterPreference } from "services/utils/filterPreferenceApi";
import { useSelector, useDispatch } from "react-redux";
import { setFilterApi } from "services/Redux/userManagament";

const FilterContext = createContext();

export const useFilter = () => {
  const context = useContext(FilterContext);
  if (!context) {
    throw new Error("useFilter must be used within a FilterProvider");
  }
  return context;
};

export const FilterProvider = ({
  children,
  userDetails,
  domain,
  subModuleId,
}) => {
  const [selectedFilters, setSelectedFilters] = useState([]);
  const [filterValues, setFilterValues] = useState({});
  const [isHydratingFilters, setIsHydratingFilters] = useState(true);
  const [anchor, setAnchor] = useState(null);
  const [activeFilter, setActiveFilter] = useState(null);
  const [hasExistingFilter, setHasExistingFilter] = useState(false);
  const [filterApiData, setFilterApiData] = useState("");
  const dispatch = useDispatch();
  const { filterApi } = useSelector((state) => state.userReducer);
  const activeFetchKeyRef = useRef("");
  const skipInitialFilterApiEffectRef = useRef(true);

  const clearFilterState = useCallback(() => {
    setSelectedFilters([]);
    setFilterValues({});
    setSortRules([]);
    setAnchor(null);
    setActiveFilter(null);
  }, []);

  // ─── Sort State ────────────────────────────────────────────────────────────
  // Each sort rule: { key: string, label: string, direction: "asc" | "desc" }
  const [sortRules, setSortRules] = useState([]);

  const getFilterDetails = useCallback(
    async (id) => {
      if (!domain || !subModuleId || !userDetails?.id) {
        setIsHydratingFilters(false);
        return;
      }

      const requestKey = `${userDetails.id}-${subModuleId}`;
      if (activeFetchKeyRef.current === requestKey) {
        return;
      }

      activeFetchKeyRef.current = requestKey;
      try {
        const res = await fetchFilterPreference(
          domain,
          userDetails?.id,
          subModuleId,
        );
        if (res.ok && res.responseDetails) {
          const data = res.responseDetails;
          setFilterApiData(data);
          setSelectedFilters(data?.filter_condition?.table_headers || []);
          setFilterValues(data?.filter_condition?.filter_values || {});
          // Restore sort rules if saved
          setSortRules(data?.filter_condition?.sort_rules || []);
          setHasExistingFilter(Object.keys(data).length > 0);
        }
      } finally {
        activeFetchKeyRef.current = "";
        setIsHydratingFilters(false);
      }
    },
    [domain, subModuleId, userDetails?.id],
  );

  useEffect(() => {
    setIsHydratingFilters(true);
    clearFilterState();
    setHasExistingFilter(false);
    setFilterApiData("");
    if (subModuleId) {
      getFilterDetails();
    } else {
      setIsHydratingFilters(false);
    }
  }, [clearFilterState, getFilterDetails, subModuleId]);

  useEffect(() => {
    if (skipInitialFilterApiEffectRef.current) {
      skipInitialFilterApiEffectRef.current = false;
      if (filterApi) {
        dispatch(setFilterApi(false));
      }
      return;
    }

    if (filterApi && subModuleId) {
      getFilterDetails();
      dispatch(setFilterApi(false));
    }
  }, [dispatch, filterApi, getFilterDetails, subModuleId]);

  const updateFilterValues = async (snapshot) => {
    // Same guard getFilterDetails already applies on read -- without it, a
    // screen whose <FilterProvider> is missing domain/subModuleId (an
    // undefined value stringifies as the literal text "undefined" in the
    // template below) fired a POST/PUT to `${undefined}filter-preference/`,
    // which the browser resolves as a *relative* URL against whatever page
    // it's on rather than throwing -- e.g. landing on
    // /admin/support/undefinedfilter-preference/ on the System Configuration
    // screen. Caught live 2026-09-14; SupportAdmin/UserRole/UserGroup were
    // fixed to pass real props, but guarding here too stops any future
    // screen with the same gap from silently doing the same thing.
    if (!domain || !subModuleId || !userDetails?.id) {
      showToast("error", "Saved preferences are unavailable for this screen.");
      return false;
    }

    const preferences = snapshot || { selectedFilters, filterValues, sortRules };

    const payload = {
      user_id: userDetails?.id,
      submodule_id: subModuleId,
      filter_condition: {
        ...(filterApiData?.filter_condition || {}),
        table_headers: preferences.selectedFilters.map(({ icon, ...rest }) => rest),
        filter_values: preferences.filterValues,
        sort_rules: preferences.sortRules,
        ...(preferences.serverQuery !== undefined ? { server_query: preferences.serverQuery } : {}),
      },
    };
    const method = hasExistingFilter ? "PUT" : "POST";
    const url = hasExistingFilter
      ? `${domain}filter-preference/${filterApiData?.id}/`
      : `${domain}filter-preference/`;

    const response = await customAPI(url, method, payload);
    if (response.ok) {
      showToast("success", "Filter values updated");
      // Keep the record ID available for subsequent saves while refreshing.
      if (response.responseDetails?.id) {
        setFilterApiData({ ...response.responseDetails, filter_condition: payload.filter_condition });
        setHasExistingFilter(true);
      } else if (hasExistingFilter) {
        setFilterApiData(previous => ({ ...previous, filter_condition: payload.filter_condition }));
      }
      dispatch(setFilterApi(true));
    }

    return response.ok;
  };

  const handleResetFilters = useCallback(async () => {
    // Clear what's on screen first -- previously the chips and filter badge
    // only cleared after the saved-preference PUT succeeded, so a failed or
    // skipped request left "Correction Type: All" and the "1" badge behind.
    clearFilterState();
    if (!filterApiData?.id) {
      return true;
    }

    const payload = {
      user_id: userDetails?.id,
      submodule_id: subModuleId,
      filter_condition: {
        ...(filterApiData?.filter_condition || {}),
        table_headers: [],
        filter_values: {},
        sort_rules: [],
        ...(filterApiData.filter_condition?.server_query !== undefined ? { server_query: {} } : {}),
      },
    };

    const response = await customAPI(
      `${domain}filter-preference/${filterApiData?.id}/`,
      "PUT",
      payload,
    );

    if (response?.ok) {
      showToast("success", "Filter values reset");
      setFilterApiData(previous => ({ ...previous, filter_condition: payload.filter_condition }));
      dispatch(setFilterApi(true));
    }
    return Boolean(response?.ok);
  }, [
    clearFilterState,
    dispatch,
    domain,
    filterApiData,
    subModuleId,
    userDetails?.id,
  ]);

  // ─── Sort Helpers ──────────────────────────────────────────────────────────

  /** Add a new sort rule for a given header field */
  const addSortRule = (header) => {
    // Prevent duplicate keys
    if (sortRules.some((r) => r.key === header.key)) return;
    setSortRules((prev) => [
      ...prev,
      { key: header.key, label: header.name, direction: "asc" },
    ]);
  };

  /** Remove a sort rule by key */
  const removeSortRule = (key) => {
    setSortRules((prev) => prev.filter((r) => r.key !== key));
  };

  /** Toggle direction of a sort rule */
  const toggleSortDirection = (key) => {
    setSortRules((prev) =>
      prev.map((r) =>
        r.key === key
          ? { ...r, direction: r.direction === "asc" ? "desc" : "asc" }
          : r,
      ),
    );
  };

  /** Update direction explicitly */
  const setSortDirection = (key, direction) => {
    setSortRules((prev) =>
      prev.map((r) => (r.key === key ? { ...r, direction } : r)),
    );
  };

  /** Reorder sort rules (drag-and-drop index swap) */
  const reorderSortRules = (fromIndex, toIndex) => {
    setSortRules((prev) => {
      const updated = [...prev];
      const [moved] = updated.splice(fromIndex, 1);
      updated.splice(toIndex, 0, moved);
      return updated;
    });
  };

  const applySortRules = useCallback(
    (data) => {
      if (!sortRules.length) return data;

      const extractNumericValue = (value) => {
        if (!value) return null;
        const str = value.toString();
        const numbers = str.match(/\d+/);
        return numbers ? parseInt(numbers[0], 10) : null;
      };

      const isIdFieldValue = (value) => {
        if (!value || typeof value !== "string") return false;
        return /^[A-Za-z]+\d+$/.test(value);
      };

      const convertTimeToMinutes = (timeStr) => {
        if (!timeStr || timeStr === "N/A" || timeStr === "") return null;

        const str = timeStr.toString().trim();

        const hmsPattern = /^(\d+):(\d{1,2}):(\d{1,2})$/;
        const hmsMatch = str.match(hmsPattern);
        if (hmsMatch) {
          const hours = parseInt(hmsMatch[1], 10);
          const minutes = parseInt(hmsMatch[2], 10);
          const seconds = parseInt(hmsMatch[3], 10);
          return hours * 60 + minutes + seconds / 60;
        }

        const hmPattern = /^(\d+):(\d{1,2})$/;
        const hmMatch = str.match(hmPattern);
        if (hmMatch) {
          const hours = parseInt(hmMatch[1], 10);
          const minutes = parseInt(hmMatch[2], 10);
          return hours * 60 + minutes;
        }

        const numPattern = /^(\d+(?:\.\d+)?)$/;
        const numMatch = str.match(numPattern);
        if (numMatch) {
          return parseFloat(numMatch[1]) * 60;
        }

        return null;
      };

      const isTimeValue = (value) => {
        if (!value || typeof value !== "string") return false;
        const str = value.toString().trim();

        return (
          /^\d+:\d{2}(?::\d{2})?$/.test(str) || /^\d+(?:\.\d+)?$/.test(str)
        );
      };

      const isDateValue = (value) => {
        if (!value || typeof value !== "string") return false;

        return (
          /^\d{2}-\d{2}-\d{4}$/.test(value) || /^\d{4}-\d{2}-\d{2}$/.test(value)
        );
      };

      const parseDate = (dateStr) => {
        if (/^\d{2}-\d{2}-\d{4}$/.test(dateStr)) {
          const [d, m, y] = dateStr.split("-");
          return new Date(`${y}-${m}-${d}`);
        }
        if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
          return new Date(dateStr);
        }
        return null;
      };

      return [...data].sort((a, b) => {
        for (const rule of sortRules) {
          let aVal = a[rule.key];
          let bVal = b[rule.key];

          if (aVal == null && bVal == null) continue;
          if (aVal == null) return 1;
          if (bVal == null) return -1;

          let cmp = 0;

          const aIsTime = isTimeValue(aVal);
          const bIsTime = isTimeValue(bVal);

          if (aIsTime && bIsTime) {
            const aMinutes = convertTimeToMinutes(aVal);
            const bMinutes = convertTimeToMinutes(bVal);

            if (aMinutes !== null && bMinutes !== null) {
              cmp = aMinutes - bMinutes;
            } else {
              cmp = aVal.toString().localeCompare(bVal.toString());
            }
          } else if (isIdFieldValue(aVal) && isIdFieldValue(bVal)) {
            const aNum = extractNumericValue(aVal);
            const bNum = extractNumericValue(bVal);

            if (aNum !== null && bNum !== null) {
              cmp = aNum - bNum;
            } else {
              cmp = aVal.toString().localeCompare(bVal.toString());
            }
          } else if (isDateValue(aVal) && isDateValue(bVal)) {
            const aDate = parseDate(aVal);
            const bDate = parseDate(bVal);

            if (aDate && bDate) {
              cmp = aDate - bDate;
            } else {
              cmp = aVal.toString().localeCompare(bVal.toString());
            }
          } else if (typeof aVal === "number" && typeof bVal === "number") {
            cmp = aVal - bVal;
          } else if (typeof aVal === "boolean" && typeof bVal === "boolean") {
            cmp = aVal === bVal ? 0 : aVal ? -1 : 1;
          } else {
            cmp = aVal
              .toString()
              .toLowerCase()
              .localeCompare(bVal.toString().toLowerCase());
          }

          if (cmp !== 0) {
            return rule.direction === "asc" ? cmp : -cmp;
          }
        }
        return 0;
      });
    },
    [sortRules],
  );

  const value = {
    // Filter State
    selectedFilters,
    filterValues,
    anchor,
    activeFilter,

    // Filter Setters
    setSelectedFilters,
    setFilterValues,
    setAnchor,
    setActiveFilter,

    // Filter Actions
    updateFilterValues,
    clearFilterState,
    handleResetFilters,

    // Sort State & Actions
    sortRules,
    isHydratingFilters,
    savedFilterCondition: filterApiData?.filter_condition,
    preferenceKey: `${domain}|${userDetails?.id}|${subModuleId}`,
    setSortRules,
    addSortRule,
    removeSortRule,
    toggleSortDirection,
    setSortDirection,
    reorderSortRules,
    applySortRules,
  };

  return (
    <FilterContext.Provider value={value}>{children}</FilterContext.Provider>
  );
};
